<template>
  <div>
    <SidbearSettingsGroup
      :active="showActiveGroup"
      title="Font Styling"
    >
      <b-form-group
        v-if="section.component === 'ElHeadline'"
        label="Headline tag"
      >
        <v-select
          v-model="headlineTag"
          :options="[{label: 'H1', value: 'h1'}, {label: 'H2', value: 'h2'}, {label: 'H3', value: 'h3'}]"
          :reduce="val => val.value"
          :searchable="false"
        />
      </b-form-group>

      <div class="position-relative">
        <color-picker-flat
          v-if="tipa && pickedColor"
          ref="colorMenuFlat"
          :color="getPickedColorAttributes(tipa.getMarkAttrs('textcolor'))"
          @input="event => applyTextColor(`rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`, tipa.commands, null)"
        />
      </div>


      <div class="position-relative">
        <color-picker-flat
          v-if="backgroundColorTip"
          ref="backgroundColorMenuFlat"
          :color="getPickedBackgroundColorAttributes(backgroundColorTip.getMarkAttrs('backgroundColor'))"
          style="top: 530px; left: 0;"
          @input="event => applyBackgroundColor(`rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`, backgroundColorTip.commands, null)"
        />
      </div>

      <div class="form-group">
        <b-form-radio-group
          v-model="resolutionSync"
          :options="[{text: 'Desktop', key: 'lg'}, {text: 'Mobile', key: 'sm'}]"
          buttons
          buttons-variant="light"
          class="resolution-style-tabs"
          size="sm"
          text-field="text"
          value-field="key"
        />
      </div>

      <div class="position-relative">
        <font-family-and-weight-control
          :text-type="config.tag"
          :family="config.resolutionStyle.lg.fontFamily"
          :weight="config.resolutionStyle.lg.fontWeight"
          @family="family => config.resolutionStyle.lg.fontFamily = family"
          @weight="setWeight"
        />

        <editor-menu-bar
          v-if="editor"
          v-slot="{ commands, isActive, getMarkAttrs}"
          :editor="editor"
        >
          <div class="menubar">
            <b-form-group
              class="!mb-[17px]"
              label="Font size"
            >
              <div class="flex w-full gap-[6px]">
                <v-select
                  v-model="fontSize"
                  :options="fontSizes()"
                  class="custom-select"
                  left
                  placeholder="Global Style"
                >
                  <template #option="{label}">
                    <div
                      v-text="label || 'Global Style'"
                    />
                  </template>
                </v-select>

                <v-select
                  v-model="fontSizeMetric"
                  :options="['px', 'em', 'pt', 'vw', 'rem']"
                  class="custom-select"
                  :searchable="false"
                  left
                >
                  <template #option="{label}">
                    <div
                      class="text-lowercase"
                      v-text="label"
                    />
                  </template>

                  <template #selected-option="{label}">
                    <div
                      class="text-lowercase"
                      v-text="label"
                    />
                  </template>
                </v-select>
              </div>
            </b-form-group>

            <b-form-group>
              <background-type
                :value="getPickedGradientColorAttributes(getMarkAttrs('textgradientcolor'))"
                :config="colorType"
                label="Color Type"
                @change="() => getMarkAttrs('textgradientcolor').raw ? backgroundGradient.backgroundGradient = JSON.parse(getMarkAttrs('textgradientcolor').raw) : null"
                @updated="val => textColorTypeChanged(commands, getMarkAttrs, val)"
              >
                <span
                  class="mt-2 block text-[#333333] underline text-[13px] cursor-pointer hover:opacity-80 line-height-[13px]"
                  @click="() => resetColorSettings(colorType, commands)"
                >
                  Reset color settigns
                </span>
              </background-type>

              <color-picker-placeholder
                v-if="!colorType.backgroundType"
                ref="colorPickerInvoker"
                :color="getPickedColorAttributes(getMarkAttrs('textcolor'))"
                @click.native="setCommands({commands, getMarkAttrs})"
              />
              <div v-else>
                <gradient-background
                  :config="backgroundGradient"
                  :direction-enabled="false"
                  @change="val => applyGradientTextColor(val, commands, getMarkAttrs('textgradientcolor').raw)"
                />
              </div>
            </b-form-group>

            <div class="form-group !mb-[18px]">
              <span
                class="relative text-[#333333] text-[13px] cursor-pointer hover:opacity-80 line-height-[13px]"
                @click="resetStyles"
              >
                Set from global styles
                <span class="absolute w-full h-[1px] bg-[#333333] left-0 bottom-0 translate-y-[-1px]" />
              </span>
            </div>

            <div class="form-group !mb-[19px]">
              <div class="flex flex-wrap gap-[10px]">
                <b-btn-group>
                  <b-btn
                    :class="{ 'is-active': isActive.bold() }"
                    variant="white"
                    @click="commands.bold"
                  >
                    <svg
                      fill="none"
                      height="14"
                      viewBox="0 0 13 14"
                      width="13"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        :fill="isActive.bold() ? '#000' : '#AEB1B4'"
                        d="M11.9766 7.47738C11.4408 6.87067 10.6462 6.44901 9.59244 6.21208C10.3309 5.88273 10.7774 5.66032 10.9321 5.54481C11.3072 5.27323 11.5899 4.96983 11.7801 4.63476C11.9706 4.29958 12.0658 3.92982 12.0658 3.52543C12.0658 3.14991 12.0034 2.79748 11.8783 2.46813C11.7533 2.13871 11.5598 1.83829 11.2979 1.56676C11.0301 1.29529 10.7264 1.07561 10.3871 0.908051C10.0716 0.757884 9.78582 0.645235 9.52983 0.570136C8.91673 0.41411 8.3482 0.336097 7.8244 0.336097H7.1636C7.04457 0.336097 6.92384 0.33464 6.80184 0.331726C6.67992 0.328994 6.61593 0.327477 6.60999 0.327477C6.58031 0.327477 6.53856 0.328994 6.48483 0.331757C6.43125 0.334671 6.38947 0.336128 6.35975 0.336128L5.95786 0.34484L2.56479 0.457488L0.243197 0.509517L0.278914 1.22878C0.778887 1.29241 1.11835 1.33008 1.29688 1.34155C1.60047 1.35889 1.80586 1.40233 1.91304 1.47157C1.97859 1.51786 2.01431 1.55249 2.02025 1.57559C2.07974 1.70269 2.11239 2.01757 2.11846 2.52028C2.14223 3.37542 2.16909 4.54251 2.19877 6.02157L2.21663 10.3289C2.21663 11.0683 2.18989 11.6289 2.13632 12.0102C2.11251 12.1489 2.04993 12.2962 1.94875 12.4522C1.6749 12.5619 1.30882 12.6516 0.850446 12.7209C0.713615 12.7381 0.511167 12.7728 0.243322 12.825L0.225464 13.6396C1.64816 13.5933 2.45774 13.5616 2.65443 13.5442C3.92826 13.469 4.81536 13.4373 5.31536 13.4488L7.07437 13.4833C7.76516 13.5064 8.35446 13.4746 8.84251 13.3881C9.61634 13.2494 10.2204 13.0759 10.6552 12.8679C11.0956 12.66 11.5123 12.3479 11.9051 11.932C12.2031 11.6143 12.4141 11.2763 12.5391 10.9179C12.7118 10.4269 12.798 9.9619 12.798 9.52269C12.7982 8.77173 12.5244 8.08986 11.9766 7.47738ZM4.98497 1.33302C5.44929 1.25793 5.83623 1.22038 6.14577 1.22038C7.16366 1.22038 7.92285 1.43708 8.42279 1.87037C8.92849 2.3036 9.18163 2.8439 9.18163 3.49095C9.18163 4.40968 8.91682 5.05676 8.38704 5.43229C7.85727 5.80778 7.07143 5.99562 6.0297 5.99562C5.63682 5.99562 5.31239 5.97543 5.05646 5.935C5.05043 5.73851 5.04746 5.5161 5.04746 5.26761L5.05646 4.4183C5.06234 3.51129 5.04449 2.70532 5.00286 2.00032C4.99091 1.80969 4.98497 1.5874 4.98497 1.33302ZM9.44973 11.3428C9.22946 11.7587 8.86622 12.0852 8.3604 12.3222C7.85433 12.5591 7.21133 12.6774 6.43159 12.6774C6.05059 12.6774 5.63388 12.5852 5.18147 12.4003C5.11004 12.2328 5.07429 12.1055 5.07417 12.0188L5.04746 9.6788L5.05646 8.1795V6.93147C5.21722 6.87371 5.51788 6.84487 5.95836 6.84487C6.95252 6.84487 7.68763 6.93733 8.16389 7.12216C8.65796 7.30721 9.07771 7.67117 9.42299 8.21429C9.66703 8.59573 9.78904 9.11565 9.78904 9.77436C9.78892 10.4155 9.67576 10.9384 9.44973 11.3428Z"
                      />
                    </svg>
                  </b-btn>
                  <b-btn
                    :class="{ 'is-active': isActive.italic() }"
                    variant="white"
                    @click="commands.italic"
                  >
                    <svg
                      fill="none"
                      height="14"
                      viewBox="0 0 10 14"
                      width="10"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        :fill="isActive.italic() ? '#000' : '#AEB1B4'"
                        d="M5.65722 10.7081C5.68453 10.6038 5.71183 10.4968 5.73557 10.3884C5.73915 10.3708 6.15226 8.55152 6.59386 6.65231C7.3536 3.38626 7.53879 2.72926 7.58034 2.62902C7.7216 2.29577 7.95071 2.04381 8.28072 1.85822C8.56563 1.69838 8.92888 1.59271 9.36098 1.54395C9.488 1.52905 9.6079 1.44912 9.69575 1.31908C9.77528 1.20122 9.82158 1.05221 9.82158 0.909973C9.82158 0.588923 9.59247 0.327477 9.31113 0.327477H3.52402C3.24268 0.327477 3.01357 0.588923 3.01357 0.909973V0.930293C3.01357 1.21748 3.194 1.4586 3.44212 1.50466C3.77094 1.56562 4.22203 1.64961 4.5758 1.84061C4.75743 1.93815 4.89868 2.05736 4.99247 2.19282C5.09931 2.3459 5.15154 2.52335 5.15154 2.73332C5.15154 3.12617 5.05064 3.52037 4.9533 3.90103C4.93312 3.98095 4.91175 4.06494 4.89157 4.14622C4.76692 4.65421 4.58292 5.425 4.37043 6.31771C3.88016 8.3727 3.27 10.9289 3.11686 11.4152C3.01358 11.7431 2.63014 12.5193 1.31128 12.6344C1.18307 12.6453 1.06317 12.7116 0.976516 12.8187C0.88867 12.927 0.841187 13.0666 0.841187 13.2142V13.2156C0.841187 13.5366 1.0703 13.7981 1.35164 13.7981H7.13995C7.42128 13.7981 7.6504 13.5366 7.6504 13.2156V13.2115C7.6504 12.9175 7.45809 12.6696 7.20286 12.6344C7.153 12.6276 7.10433 12.6209 7.0521 12.6141C6.54402 12.5477 5.91249 12.4637 5.64658 12.0695C5.52313 11.8866 5.48395 11.6455 5.5243 11.3353C5.55038 11.1254 5.60263 10.9222 5.65722 10.7081Z"
                      />
                    </svg>
                  </b-btn>
                  <b-btn
                    :class="{ 'is-active': isActive.underline() }"
                    variant="white"
                    @click="commands.underline"
                  >
                    <svg
                      fill="none"
                      height="16"
                      viewBox="0 0 15 16"
                      width="15"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        :fill="isActive.underline() ? '#000' : '#AEB1B4'"
                        d="M0.517612 1.31839C1.27851 1.33713 1.73685 1.45882 1.89265 1.6828C1.99861 1.8389 2.05169 2.50295 2.05169 3.67535V6.79053C2.05169 7.76975 2.10149 8.50867 2.20126 9.00761C2.33861 9.74968 2.59739 10.3579 2.97779 10.8317C3.35183 11.2995 3.91011 11.6801 4.65216 11.9731C5.38184 12.2599 6.2674 12.4033 7.3088 12.4033C8.21923 12.4033 9.01447 12.3006 9.6944 12.0947C10.3615 11.8951 10.9291 11.6175 11.3967 11.2621C11.8584 10.9005 12.2076 10.5169 12.4444 10.1114C12.6065 9.82461 12.7346 9.44405 12.8281 8.97043C12.9591 8.29071 13.006 7.34243 12.9685 6.12641C12.8813 3.77515 12.8126 2.5404 12.7626 2.42189C12.7626 2.35338 12.7579 2.25669 12.7487 2.13198C12.7393 2.00718 12.7346 1.92314 12.7346 1.87943C12.7346 1.71727 12.7815 1.58933 12.875 1.49588C12.9126 1.45849 13.1587 1.40538 13.614 1.33677C13.8573 1.33055 14.1193 1.28988 14.3999 1.21518C14.425 1.06554 14.4372 0.968885 14.4372 0.925143C14.4372 0.844082 14.4188 0.685008 14.3813 0.448148L14.2129 0.42944C13.4644 0.510533 12.8533 0.541627 12.3792 0.522951L10.4617 0.42944H9.67592L9.65708 1.23402L9.78814 1.262L10.7236 1.24333C10.9916 1.2371 11.2318 1.34316 11.4438 1.56148C11.5622 1.69244 11.6372 1.96675 11.6683 2.3846L11.7055 2.93666C11.7432 3.47296 11.7852 3.97024 11.832 4.42856C11.8789 4.88694 11.9132 5.26895 11.935 5.57445C11.9567 5.88012 11.9677 6.27923 11.9677 6.77192C11.9677 7.74485 11.9022 8.45867 11.7712 8.91391C11.6654 9.27563 11.4999 9.6313 11.2756 9.98048C11.1693 10.1551 10.9667 10.3547 10.6673 10.5793C10.3993 10.785 10.0907 10.944 9.74132 11.0562C9.19864 11.2309 8.60314 11.3181 7.95469 11.3181C7.39946 11.3181 6.8478 11.1717 6.29892 10.8784C5.91852 10.6727 5.61922 10.3859 5.40094 10.0179C5.12037 9.53159 4.9613 8.90146 4.92385 8.1281L4.79289 5.50892L4.78335 3.36693C4.78335 3.32965 4.78175 3.25009 4.77864 3.1285C4.77549 3.00691 4.77392 2.90576 4.77392 2.82457C4.77392 2.0887 4.81445 1.67717 4.89548 1.58982C5.01412 1.43385 5.26038 1.35591 5.63455 1.35591C6.03379 1.35591 6.42038 1.32789 6.79455 1.27177V1.18756L6.77588 0.588907L6.78515 0.45788C6.59813 0.47033 6.33002 0.476621 5.98074 0.476621C5.79372 0.482847 5.33839 0.498443 4.61498 0.523377C4.10345 0.542119 3.57964 0.551359 3.04335 0.551359C2.83126 0.551359 2.31368 0.529668 1.49046 0.485927C1.16619 0.461025 0.816918 0.448607 0.442744 0.448607C0.274333 0.448607 0.149598 0.451622 0.0686035 0.457912L0.0966829 1.28117C0.146518 1.29349 0.28685 1.30601 0.517612 1.31839Z"
                      />
                      <path
                        :fill="isActive.underline() ? '#000' : '#AEB1B4'"
                        d="M14.138 13.9998H0.367917C0.28063 13.9998 0.208873 14.0395 0.152746 14.1191C0.0965526 14.1987 0.0686035 14.3002 0.0686035 14.4239V15.2721C0.0686035 15.3959 0.0966836 15.4975 0.152746 15.577C0.208873 15.6566 0.280499 15.6961 0.367917 15.6961H14.138C14.2254 15.6961 14.2972 15.6565 14.3533 15.577C14.4093 15.4975 14.4372 15.3959 14.4372 15.2721V14.4239C14.4372 14.3002 14.4093 14.1986 14.3533 14.1191C14.2972 14.0395 14.2254 13.9998 14.138 13.9998Z"
                      />
                    </svg>
                  </b-btn>
                  <b-btn
                    v-if="config.hasOwnProperty('mods') && config.mods.includes('strike')"
                    :class="{ 'is-active': isActive.strike() }"
                    variant="white"
                    @click="commands.strike"
                  >
                    <i class="fa fa-strikethrough" />
                  </b-btn>
                </b-btn-group>

                <b-btn-group v-if="!hide.includes('align')">
                  <b-btn
                    :class="{ 'is-active': align === 'left' }"
                    variant="white"
                    @click="align = 'left'"
                  >
                    <i class="fa fa-align-left" />
                  </b-btn>
                  <b-btn
                    :class="{ 'is-active': align === 'center' }"
                    variant="white"
                    @click="align = 'center'"
                  >
                    <i class="fa fa-align-center" />
                  </b-btn>
                  <b-btn
                    :class="{ 'is-active': align === 'right' }"
                    variant="white"
                    @click="align = 'right'"
                  >
                    <i class="fa fa-align-right" />
                  </b-btn>
                  <b-btn
                    :class="{ 'is-active': align === 'justify' }"
                    variant="white"
                    @click="align = 'justify'"
                  >
                    <i class="fa fa-align-justify" />
                  </b-btn>
                </b-btn-group>

                <b-btn-group
                  v-if="config.hasOwnProperty('mods') && config.mods.includes('list')"
                  class="ml-3 mb-2"
                >
                  <b-btn
                    :class="{ 'is-active': isActive.bullet_list() }"
                    variant="white"
                    @click="commands.bullet_list"
                  >
                    <i class="fa fa-list-ul" />
                  </b-btn>

                  <b-btn
                    v-b-tooltip="'Orderd list'"
                    :class="{ 'is-active': isActive.ordered_list() }"
                    variant="white"
                    @click="commands.ordered_list"
                  >
                    <i class="fa fa-list-ol" />
                  </b-btn>
                </b-btn-group>
              </div>
            </div>

            <b-form-group
              label="Text Transform"
              class="mb-4"
            >
              <b-btn-group class="mb-2">
                <b-btn
                  variant="white"
                  @click="() => editor.state.selection.empty ? textTransform = 'uppercase' : commands.textTransform({textTransform: 'uppercase'})"
                >
                  <svg
                    width="21"
                    height="12"
                    viewBox="0 0 21 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.28906 8.22656L4.75781 4.52344H4.625L3.17969 8.22656H6.28906ZM0.289062 11.5938C0.674479 11.5677 0.984375 11.3932 1.21875 11.0703C1.36979 10.8672 1.58594 10.4193 1.86719 9.72656L5.42188 0.96875H5.86719L9.4375 9.375C9.83854 10.3177 10.138 10.9271 10.3359 11.2031C10.5339 11.474 10.8151 11.6042 11.1797 11.5938V12H6V11.5938C6.52083 11.5729 6.86198 11.5286 7.02344 11.4609C7.1901 11.3932 7.27344 11.2214 7.27344 10.9453C7.27344 10.8203 7.23177 10.6302 7.14844 10.375C7.09635 10.224 7.02604 10.0417 6.9375 9.82812L6.53125 8.85938H2.9375C2.70312 9.49479 2.55208 9.90885 2.48438 10.1016C2.34375 10.513 2.27344 10.8125 2.27344 11C2.27344 11.2292 2.42708 11.3958 2.73438 11.5C2.91667 11.5573 3.1901 11.5885 3.55469 11.5938V12H0.289062V11.5938Z"
                      :fill="(editor.state.selection.empty ? textTransform : getMarkAttrs('textTransform').textTransform) === 'uppercase'? '#000' : '#AEB1B4'"
                    />
                    <path
                      d="M11.2578 11.5938C11.6901 11.5781 12 11.5208 12.1875 11.4219C12.5052 11.2552 12.6641 10.9375 12.6641 10.4688V2.72656C12.6641 2.25781 12.5156 1.94271 12.2188 1.78125C12.0417 1.68229 11.7214 1.61979 11.2578 1.59375V1.1875H16.2031C17.1562 1.1875 17.9479 1.29688 18.5781 1.51562C19.7656 1.92708 20.3594 2.67708 20.3594 3.76562C20.3594 4.42708 20.1172 4.95312 19.6328 5.34375C19.1536 5.72917 18.6328 5.97396 18.0703 6.07812V6.22656C18.6641 6.34115 19.1797 6.53385 19.6172 6.80469C20.4714 7.33594 20.8984 8.07552 20.8984 9.02344C20.8984 9.90885 20.4896 10.6276 19.6719 11.1797C18.8542 11.7266 17.7656 12 16.4062 12H11.2578V11.5938ZM15.2188 6.03906C16.2083 6.03906 16.8724 5.8776 17.2109 5.55469C17.5495 5.23177 17.7188 4.65885 17.7188 3.83594C17.7188 3.22656 17.6068 2.71354 17.3828 2.29688C17.1641 1.88021 16.7396 1.67188 16.1094 1.67188C15.776 1.67188 15.5443 1.72917 15.4141 1.84375C15.2839 1.95312 15.2188 2.15365 15.2188 2.44531V6.03906ZM15.2188 10.5234C15.224 10.8047 15.2656 11.0104 15.3438 11.1406C15.474 11.3698 15.724 11.4844 16.0938 11.4844C16.849 11.4844 17.3776 11.2786 17.6797 10.8672C17.987 10.4557 18.1406 9.86979 18.1406 9.10938C18.1406 7.97396 17.8047 7.21875 17.1328 6.84375C16.7266 6.61458 16.0885 6.50781 15.2188 6.52344V10.5234Z"
                      :fill="(editor.state.selection.empty ? textTransform : getMarkAttrs('textTransform').textTransform) === 'uppercase' ? '#000' : '#AEB1B4'"
                    />
                  </svg>
                </b-btn>
                <b-btn
                  variant="white"
                  @click="() => editor.state.selection.empty ? textTransform = 'capitalize' : commands.textTransform({textTransform: 'capitalize'})"
                >
                  <svg
                    width="20"
                    height="18"
                    viewBox="0 0 20 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.28906 10.2266L4.75781 6.52344H4.625L3.17969 10.2266H6.28906ZM0.289062 13.5938C0.674479 13.5677 0.984375 13.3932 1.21875 13.0703C1.36979 12.8672 1.58594 12.4193 1.86719 11.7266L5.42188 2.96875H5.86719L9.4375 11.375C9.83854 12.3177 10.138 12.9271 10.3359 13.2031C10.5339 13.474 10.8151 13.6042 11.1797 13.5938V14H6V13.5938C6.52083 13.5729 6.86198 13.5286 7.02344 13.4609C7.1901 13.3932 7.27344 13.2214 7.27344 12.9453C7.27344 12.8203 7.23177 12.6302 7.14844 12.375C7.09635 12.224 7.02604 12.0417 6.9375 11.8281L6.53125 10.8594H2.9375C2.70312 11.4948 2.55208 11.9089 2.48438 12.1016C2.34375 12.513 2.27344 12.8125 2.27344 13C2.27344 13.2292 2.42708 13.3958 2.73438 13.5C2.91667 13.5573 3.1901 13.5885 3.55469 13.5938V14H0.289062V13.5938Z"
                      :fill="(editor.state.selection.empty ? textTransform : getMarkAttrs('textTransform').textTransform) === 'capitalize' ? '#000' : '#AEB1B4'"
                    />
                    <path
                      d="M14.3672 12.4453C14.388 12.737 14.4453 12.9766 14.5391 13.1641C14.7266 13.5286 15.0391 13.7109 15.4766 13.7109C16.0234 13.7109 16.4089 13.4219 16.6328 12.8438C16.862 12.2656 16.9766 11.4479 16.9766 10.3906C16.9766 9.55208 16.875 8.83073 16.6719 8.22656C16.474 7.6224 16.0833 7.32031 15.5 7.32031C15.1562 7.32031 14.8802 7.44792 14.6719 7.70312C14.4688 7.95312 14.3672 8.13281 14.3672 8.24219V12.4453ZM14.3672 3.1875V7.33594C14.6172 7.09115 14.849 6.90625 15.0625 6.78125C15.4531 6.55729 15.875 6.44531 16.3281 6.44531C17.2292 6.44531 17.9531 6.79688 18.5 7.5C19.0521 8.20312 19.3281 9.06771 19.3281 10.0938C19.3281 11.4167 18.9766 12.4375 18.2734 13.1562C17.5755 13.8698 16.7057 14.2266 15.6641 14.2266C15.237 14.2266 14.8594 14.1432 14.5312 13.9766C14.2031 13.8099 13.8958 13.5911 13.6094 13.3203L12.3516 14.2266H12.125V4.5C12.125 4.16667 12.0651 3.94271 11.9453 3.82812C11.8255 3.70833 11.6016 3.625 11.2734 3.57812V3.1875H14.3672Z"
                      :fill="(editor.state.selection.empty ? textTransform : getMarkAttrs('textTransform').textTransform) === 'capitalize' ? '#000' : '#AEB1B4'"
                    />
                  </svg>
                </b-btn>
                <b-btn
                  variant="white"
                  @click="() => editor.state.selection.empty ? textTransform = 'lowercase' : commands.textTransform({textTransform: 'lowercase'})"
                >
                  <svg
                    width="17"
                    height="12"
                    viewBox="0 0 17 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.50781 10.0859C3.76823 10.0859 4.01042 10.0078 4.23438 9.85156C4.35938 9.75781 4.50781 9.61198 4.67969 9.41406V7.07812C4.2526 7.18229 3.8776 7.34635 3.55469 7.57031C3.00781 7.95052 2.73438 8.4349 2.73438 9.02344C2.73438 9.3724 2.8125 9.63802 2.96875 9.82031C3.13021 9.9974 3.3099 10.0859 3.50781 10.0859ZM0.40625 9.50781C0.40625 8.78906 0.752604 8.21875 1.44531 7.79688C2.13802 7.375 3.22396 6.95052 4.70312 6.52344V5.35938C4.70312 4.88542 4.60156 4.53646 4.39844 4.3125C4.20052 4.08333 3.84635 3.96875 3.33594 3.96875C3.04948 3.96875 2.8125 4.02344 2.625 4.13281C2.44271 4.23698 2.35156 4.36979 2.35156 4.53125C2.35156 4.59375 2.36198 4.64583 2.38281 4.6875C2.40365 4.72917 2.4349 4.77344 2.47656 4.82031L2.57031 4.92969C2.64323 5.01823 2.70052 5.11719 2.74219 5.22656C2.78906 5.33073 2.8125 5.44271 2.8125 5.5625C2.8125 5.86458 2.71094 6.09896 2.50781 6.26562C2.3099 6.42708 2.07812 6.50781 1.8125 6.50781C1.50521 6.50781 1.24219 6.41667 1.02344 6.23438C0.809896 6.05208 0.703125 5.78906 0.703125 5.44531C0.703125 4.83073 0.992188 4.34375 1.57031 3.98438C2.14844 3.625 2.85938 3.44531 3.70312 3.44531C4.60938 3.44531 5.36719 3.61979 5.97656 3.96875C6.58073 4.32292 6.88281 4.97396 6.88281 5.92188V9.79688C6.88281 9.91146 6.91146 10.0104 6.96875 10.0938C7.03125 10.1719 7.09896 10.2109 7.17188 10.2109C7.23438 10.2109 7.28906 10.2005 7.33594 10.1797C7.38802 10.1536 7.46615 10.0885 7.57031 9.98438L7.8125 10.3203C7.5 10.6849 7.15365 10.9401 6.77344 11.0859C6.53385 11.1797 6.28125 11.2266 6.01562 11.2266C5.54167 11.2266 5.19531 11.0885 4.97656 10.8125C4.85156 10.6562 4.76042 10.4245 4.70312 10.1172C4.26042 10.5599 3.76042 10.8724 3.20312 11.0547C2.86458 11.1693 2.53125 11.2266 2.20312 11.2266C1.76562 11.2266 1.35677 11.0885 0.976562 10.8125C0.596354 10.5312 0.40625 10.0964 0.40625 9.50781Z"
                      :fill="(editor.state.selection.empty ? textTransform : getMarkAttrs('textTransform').textTransform) === 'lowercase' ? '#000' : '#AEB1B4'"
                    />
                    <path
                      d="M11.3672 9.44531C11.388 9.73698 11.4453 9.97656 11.5391 10.1641C11.7266 10.5286 12.0391 10.7109 12.4766 10.7109C13.0234 10.7109 13.4089 10.4219 13.6328 9.84375C13.862 9.26562 13.9766 8.44792 13.9766 7.39062C13.9766 6.55208 13.875 5.83073 13.6719 5.22656C13.474 4.6224 13.0833 4.32031 12.5 4.32031C12.1562 4.32031 11.8802 4.44792 11.6719 4.70312C11.4688 4.95312 11.3672 5.13281 11.3672 5.24219V9.44531ZM11.3672 0.1875V4.33594C11.6172 4.09115 11.849 3.90625 12.0625 3.78125C12.4531 3.55729 12.875 3.44531 13.3281 3.44531C14.2292 3.44531 14.9531 3.79688 15.5 4.5C16.0521 5.20312 16.3281 6.06771 16.3281 7.09375C16.3281 8.41667 15.9766 9.4375 15.2734 10.1562C14.5755 10.8698 13.7057 11.2266 12.6641 11.2266C12.237 11.2266 11.8594 11.1432 11.5312 10.9766C11.2031 10.8099 10.8958 10.5911 10.6094 10.3203L9.35156 11.2266H9.125V1.5C9.125 1.16667 9.0651 0.942708 8.94531 0.828125C8.82552 0.708333 8.60156 0.625 8.27344 0.578125V0.1875H11.3672Z"
                      :fill="(editor.state.selection.empty ? textTransform : getMarkAttrs('textTransform').textTransform) === 'lowercase' ? '#000' : '#AEB1B4'"
                    />
                  </svg>
                </b-btn>
              </b-btn-group>

              <div>
                <span
                  class="text-[#333333] underline text-[13px] cursor-pointer hover:opacity-80 line-height-[13px]"
                  @click="editor.state.selection.empty ? textTransform = null : commands.textTransform({textTransform: null})"
                >
                  Set to Default
                </span>
              </div>
            </b-form-group>

            <b-form-group
              label="Line height"
              class="!mb-[21px]"
              label-class="!pb-[7px] !mb-[-6px]"
            >
              <div class="flex items-center w-full gap-[6px]">
                <div class="w-[172px] pl-[8px]">
                  <vue-slider
                    v-model="lineHeight"
                    :dot-size="17"
                    :interval="0.1"
                    :max="4"
                    :min="0.1"
                    class="editor-slider-1"
                    tooltip="none"
                  />
                </div>

                <div class="w-[56px]">
                  <b-input
                    v-model="lineHeight"
                    class="h-[29px]"
                    min="0"
                    size="sm"
                    step=".1"
                    type="number"
                  />
                </div>
              </div>

              <div class="form-group !mb-[18px]">
                <span
                  class="relative text-[#333333] text-[13px] cursor-pointer hover:opacity-80 line-height-[13px]"
                  @click="() => lineHeight = null"
                >
                  Set from global styles
                  <span class="absolute w-full h-[1px] bg-[#333333] left-0 bottom-0 translate-y-[-1px]" />
                </span>
              </div>
            </b-form-group>

            <b-form-group
              label="Letter spacing"
              label-class="!pb-[7px] !mb-[-6px]"
            >
              <div class="flex items-center w-full gap-[6px]">
                <div class="w-[172px] pl-[8px]">
                  <vue-slider
                    v-model="letterSpacing"
                    :dot-size="17"
                    :max="20"
                    class="editor-slider-1"
                    tooltip="none"
                  />
                </div>

                <div class="w-[56px]">
                  <b-input
                    v-model="letterSpacing"
                    class="h-[29px]"
                    min="0"
                    :max="20"
                    size="sm"
                    step=".1"
                    type="number"
                  />
                </div>
              </div>

              <div class="form-group !mb-[18px]">
                <span
                  class="relative text-[#333333] text-[13px] cursor-pointer hover:opacity-80 line-height-[13px]"
                  @click="() => letterSpacing = null"
                >
                  Set from global styles
                  <span class="absolute w-full h-[1px] bg-[#333333] left-0 bottom-0 translate-y-[-1px]" />
                </span>
              </div>
            </b-form-group>

            <b-form-group
              label="Rotate"
              label-class="!pb-[3px]"
            >
              <b-row
                class="align-items-center pl-2 gap-[10px]"
                no-gutters
              >
                <b-col
                  cols="8"
                >
                  <vue-slider
                    v-model="rotate"
                    :interval="1"
                    :max="360"
                    :min="0"
                    class="editor-slider-1"
                    tooltip="none"
                  />
                </b-col>
                <b-col>
                  <b-input
                    v-model="rotate"
                    :max="360"
                    :min="0"
                    :step="1"
                    size="sm"
                    type="number"
                  />
                </b-col>
              </b-row>
            </b-form-group>

            <b-form-group
              class="!mb-[17px]"
              label="Background effect"
            >
              <v-select
                v-model="bgEffect"
                :options="['Select effect', 'Background color', 'Background mark']"
                :searchable="false"
              />
            </b-form-group>

            <b-form-group>
              <tiptap-background-color
                v-if="bgEffect === 'Background mark' || isActive.background()"
                :commands="commands"
                :config="backgroundMark"
                :is-active="isActive.background()"
                :marks="() => getMarkAttrs('background')"
              />

              <div
                v-else-if="bgEffect === 'Background color' || isActive.backgroundColor()"
              >
                <div class="flex items-center gap-[10px]">
                  <color-picker-placeholder
                    ref="backgroundColorPickerInvoker"
                    :color="getPickedBackgroundColorAttributes(getMarkAttrs('backgroundColor'))"
                    class="mb-2"
                    @click.native="setBgCommands({commands, getMarkAttrs})"
                  />
                  <label class="mb-[5px] pb-0">Color</label>
                </div>

                <b-link
                  v-if="isActive.backgroundColor()"
                  @click="commands.backgroundColor(false)"
                >
                  Remove color
                </b-link>
              </div>
            </b-form-group>

            <div class="position-relative">
              <color-picker-flat
                v-if="backgroundMark.color"
                ref="backgroundMarkColorMenuFlat"
                :color="backgroundMark.color"
                style="top: -20px;left:0;"
                @input="event => backgroundMark.color = event.hex8"
              />
            </div>

            <div class="">
              <div class="d-flex align-items-center">
                <b-btn
                  :class="{ 'is-active': isActive.link() || linkMenuIsActive }"
                  class="h-[38px] w-[41px] flex items-center justify-center p-0"
                  variant="white"
                  @click="linkMenuIsActive = !linkMenuIsActive"
                >
                  <svg
                    class="inline"
                    fill="none"
                    height="17"
                    viewBox="0 0 18 17"
                    width="18"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.8985 1.3258C14.336 -0.138704 11.8029 -0.138704 10.2404 1.32577L6.89657 4.45995C5.28039 5.97486 5.43002 8.38861 6.89657 9.76327C7.14216 9.99346 7.41388 10.1811 7.70081 10.3353L8.31111 9.76327C8.7114 9.38802 8.5705 8.94936 8.5633 8.63283C8.47544 8.57446 8.38999 8.51136 8.31111 8.43743C7.55851 7.73208 7.52467 6.52289 8.31111 5.78577C8.4279 5.6763 11.584 2.71808 11.6549 2.65164C12.435 1.92042 13.7038 1.92042 14.4839 2.65164C15.2641 3.38286 15.2641 4.57208 14.4839 5.3033L12.2737 7.37496C12.3376 7.70643 12.7212 8.49161 12.5239 9.78687C12.5335 9.77799 12.5451 9.77227 12.5547 9.7633L15.8985 6.62911C17.4609 5.16461 17.4609 2.7903 15.8985 1.3258Z"
                      fill="#6E747A"
                    />
                    <path
                      d="M10.4328 6.44871C10.1873 6.21852 9.91552 6.03083 9.62865 5.87668L9.01836 6.44871C8.618 6.8239 8.7589 7.26255 8.7661 7.57909C8.85402 7.63746 8.93947 7.70055 9.01836 7.77449C9.77095 8.4799 9.80479 9.68902 9.01836 10.4262C8.9013 10.5359 5.48376 13.7391 5.41538 13.8032C4.63525 14.5344 3.36647 14.5344 2.58634 13.8032C1.80621 13.072 1.80621 11.8828 2.58634 11.1516L5.05577 8.83696C4.99186 8.50549 4.60824 7.7203 4.80558 6.42505C4.79595 6.43393 4.78435 6.43971 4.77474 6.44868L1.1718 9.82577C-0.390601 11.2903 -0.390601 13.6646 1.1718 15.1291C2.73427 16.5935 5.26738 16.5935 6.82985 15.1291L10.4328 11.752C12.019 10.2653 11.9323 7.85418 10.4328 6.44871Z"
                      fill="#6E747A"
                    />
                  </svg>
                </b-btn>

                <label class="mb-0 mt-2 pl-[12px]">Text Link</label>

                <template v-if="linkMenuIsActive">
                  <b-btn
                    class="ml-auto text-danger"
                    variant="link"
                    @click="deleteLinkUrl(commands)"
                  >
                    <i class="fa fa-trash" />
                  </b-btn>
                </template>
              </div>

              <template v-if="linkMenuIsActive">
                <tiptap-link
                  :value="getLinkAttributes(getMarkAttrs('link'))"
                  class="mt-2"
                  @change="link => linkAttrs = link"
                >
                  <b-btn
                    block
                    variant="primary"
                    @click="setLinkUrl(commands.link, linkAttrs)"
                  >
                    {{ linkSaveText }}
                  </b-btn>
                </tiptap-link>
              </template>
            </div>
          </div>
        </editor-menu-bar>
      </div>
    </SidbearSettingsGroup>

    <SidbearSettingsGroup title="Text Shadow">
      <text-shadow-control
        :config="config"
        class="!mb-[19px]"
      />
    </SidbearSettingsGroup>

    <template>
      <SidbearSettingsGroup
        v-if="!hide.includes('animations')"
        title="Text Animations"
      >
        <animations :animations="animations" />
      </SidbearSettingsGroup>

      <SidbearSettingsGroup
        v-if="!hide.includes('spacing')"
        :title="`${groupPrefix} Padding & Margins`"
      >
        <padding-full-customs
          :config="padding"
        />

        <margin-full-customs
          :config="margin"
        />
      </SidbearSettingsGroup>
    </template>
  </div>
</template>

<script>
import shortid from 'shortid'
import _ from 'lodash'
import ClickOutside from '@/directives/click-outside'

import {mapState} from 'vuex'
import ModMixin from '@builder/components/mixins/ModMixin'
import Animations from '../controls/Animations'
import TiptapLink from '../controls/TiptapLink'
import ColorPickerFlat from '../utils/ColorPickerFlat'
import ColorPickerPlaceholder from '../utils/ColorPickerPlaceholder'
import TextShadowControl from '../controls/TextShadowControl'
import TiptapBackgroundColor from '../controls/TiptapBackgroundColor'
import SidbearSettingsGroup from '@/components/editor/components/SidbearSettingsGroup'
import FontFamilyAndWeightControl from '@/components/editor/controls/FontFamilyAndWeightControl'
import GlobalStyleTextMixin from '@/components/mixins/GlobalStyleTextMixin'
import BackgroundType from '@/components/editor/controls/BackgroundType'

// Local compatibility EditorMenuBar for Tiptap 3
const EditorMenuBar = {
  name: 'EditorMenuBarCompat',
  props: {
    editor: {
      type: Object,
      required: true
    }
  },
  computed: {
    context () {
      const editor = this.editor

      if (!editor) {
        return null
      }

      const isActive = typeof Proxy !== 'undefined'
        ? new Proxy(
          {},
          {
            get (_target, name) {
              return (attrs) => editor.isActive(name, attrs)
            }
          }
        )
        : {
          bold: (attrs) => editor.isActive('bold', attrs),
          italic: (attrs) => editor.isActive('italic', attrs),
          underline: (attrs) => editor.isActive('underline', attrs),
          strike: (attrs) => editor.isActive('strike', attrs),
          bullet_list: (attrs) => editor.isActive('bullet_list', attrs),
          ordered_list: (attrs) => editor.isActive('ordered_list', attrs),
          link: (attrs) => editor.isActive('link', attrs),
          background: (attrs) => editor.isActive('background', attrs),
          backgroundColor: (attrs) => editor.isActive('backgroundColor', attrs)
        }

      const getAttrs = (name) => (editor ? editor.getAttributes(name) || {} : {})

      return {
        editor,
        commands: editor.commands,
        isActive,
        getMarkAttrs: getAttrs,
        getNodeAttrs: getAttrs
      }
    }
  },
  render () {
    if (!this.editor || !this.$scopedSlots.default || !this.context) {
      return null
    }

    return this.$scopedSlots.default(this.context)
  }
}

export default {
  name: 'TiptapMod',

  directives: {ClickOutside},

  components: {
    BackgroundType,
    FontFamilyAndWeightControl,
    SidbearSettingsGroup,
    TiptapBackgroundColor,
    TextShadowControl, ColorPickerPlaceholder, ColorPickerFlat, TiptapLink, Animations, EditorMenuBar
  },

  mixins: [ModMixin, GlobalStyleTextMixin],

  data () {
    return {
      tipa: null,
      backgroundMark: {
        color: null
      },
      backgroundColorTip: null,
      pickedColor: '',
      localFontWeight: null,
      backgroundColor: '',
      bgEffect: 'Select effect',
      linkMenuIsActive: false,
      linkAttrs: {},
      linkSaveText: 'Save changes',
      linkDeleteText: 'Delete link',
      colorType: {
        backgroundType: null
      },
      backgroundGradient: {
        backgroundGradient: [
          {
            'color': 'rgb(119,171,255)',
            'position': 0
          },
          {
            'color': 'rgb(0,60,152)',
            'position': 100
          }
        ]
      }
    }
  },

  computed: {
    gradientColor: {
      get () {
        return _.get(this.config, ['gradientColor', this.resolutioner], _.get(this.config, ['gradientColor', 'lg'], false))
      },
      set (val) {
        this.$set(this.config, 'gradientColor', {..._.set(this.config['gradientColor'] || {}, [this.resolutioner], val)})
      }
    },
    padding () {
      if (!this.config.padding) {
        this.$set(this.config, 'padding', {})
      }

      return this.config.padding
    },
    margin () {
      if (!this.config.margin) {
        this.$set(this.config, 'margin', {})
      }

      return this.config.margin
    },
    align: {
      get () {
        return _.get(this.config.resolutionStyle, [this.resolutioner, 'align'], '')
      },
      set (val) {
        this.$set(this.config.resolutionStyle, this.resolutioner, {
          ..._.get(this.config.resolutionStyle, [this.resolutioner], {}),
          align: val
        })
      }
    },
    ...mapState('editor', {
      googleFonts: state => state.globalStyles.font.addedGoogleFonts
    }),
    fonts () {
      return [
        '',
        ...this.googleFonts.map(font => font.family)
      ]
    },
    letterSpacing: {
      get () {
        const _letterSpacing = _.get(this.config.resolutionStyle, [this.resolutioner, 'letterSpacing'], null)
        return _letterSpacing === null ? _.get(this.globalStyles, ['textStyles', this.config.tag || 'paragraph', 'letterSpacing', this.resolutioner], 0) : _letterSpacing || 0
      },
      set (val) {
        this.$set(this.config.resolutionStyle[this.resolutioner], 'letterSpacing', val)
      }
    },
    textTransform: {
      get () {
        return _.get(this.config, ['textTransform', this.resolutioner], null) || _.get(this.config, ['textTransform', 'lg'], null)
      },
      set (val) {
        this.$set(this.config, 'textTransform', {..._.set(this.config.textTransform || {}, [this.resolutioner], val)})
      }
    },
    fontSize: {
      get () {
        return _.get(this.config.resolutionStyle, [this.resolutioner, 'fontSize'], null) || this.config.resolutionStyle.lg.fontSize
      },
      set (val) {
        const _resolutionStyle = _.get(this.config.resolutionStyle, this.resolutioner, null)
        this.$set(this.config.resolutionStyle, this.resolutioner, {..._.set(_resolutionStyle || {}, ['fontSize'], val)})
      }
    },
    lineHeight: {
      get () {
        return _.get(this.config.resolutionStyle, [this.resolutioner, 'lineHeight'], null) || this.config.resolutionStyle.lg.lineHeight || 1.3
      },
      set (val) {
        this.$set(this.config.resolutionStyle, this.resolutioner, {..._.set(this.config.resolutionStyle[this.resolutioner] || {}, 'lineHeight', val)})
      }
    },
    animations: {
      get () {
        if (!Object.prototype.hasOwnProperty.call(this.config, 'animations')) {
          this.$set(this.config, 'animations', {
            animation: null,
            once: true,
            offset: 200,
            delay: 50
          })
        }

        return this.config.animations
      },
      set (val) {
        this.config.animations = val
      }
    },
    ...mapState('editor', {
      editor: state => state.tiptap,
      openedItem: state => state.openedItem
    }),
    rotate: {
      get () {
        return parseInt(_.get(this.config, ['rotate', this.resolutioner], _.get(this.config, ['rotate', 'lg'], 0)))
      },
      set (val) {
        this.$set(this.config, 'rotate', {..._.set(this.config['rotate'] || {}, [this.resolutioner], val)})
      }
    },
    headlineTag: {
      get () {
        return this.config.tag || 'h3'
      },
      set (val) {
        this.$set(this.config, 'tag', val)
      }
    },
    fontSizeMetric: {
      get () {
        return _.get(this.config, ['fontSizeMetric', this.resolutioner], _.get(this.config, ['fontSizeMetric', 'lg'], 'px'))
      },
      set (val) {
        this.$set(this.config, 'fontSizeMetric', {..._.set(this.config['fontSizeMetric'] || {}, [this.resolutioner], val)})
      }
    }
  },

  watch: {
    openedItem (val) {
      if (val === this.componentName) {
        VEvent.fire(this.section.uid + '_' + this.componentName)
      }
    },
    fontFamily () {
      this.config.resolutionStyle.lg.fontWeight = 400
    }
  },

  mounted () {
    VEvent.listen('tiptap-mod-color', () => {
      this.linkMenuIsActive = false
    })
    VEvent.listen('tiptap-mod-font-weight', fontWeight => this.localFontWeight = fontWeight)

    document.addEventListener('mousedown', this.onMouseDown, true)
    window.addEventListener('highlighter-click', this.onSelectionElement)
  },

  destroyed () {
    document.removeEventListener('mousedown', this.onMouseDown, true)
    window.removeEventListener('highlighter-click', this.onSelectionElement)
  },

  methods: {
    onSelectionElement (e) {
      const _targetDom = e.detail.closest('.ProseMirror')

      if (_targetDom) {
        if (this.editor.view.dom !== _targetDom) {
          this.editor.setSelection()
        }
      }
    },
    textColorTypeChanged (commands, getMarkAttrs, val) {
      const {state} = this.editor
      const empty = state.selection.empty

      setTimeout(() => {
        if (val.value) {
          if (!empty && !getMarkAttrs('textgradientcolor').raw) {
            this.applyGradientTextColor(this.backgroundGradient, commands)
            setTimeout(() => {
              // window.getSelection()?.removeAllRanges()
            }, 200)
            return
          }

          if (empty && !this.config.resolutionStyle[this.resolutioner].backgroundGradient) {
            this.applyGradientTextColor(this.backgroundGradient, commands)
          }
        } else {
          if (!empty && getMarkAttrs('textgradientcolor').raw) {
            this.applyGradientTextColor({backgroundGradient: null}, commands)
            setTimeout(() => {
              // window.getSelection()?.removeAllRanges()
            }, 200)
            return
          }

          if (empty) {
            return this.applyGradientTextColor({backgroundGradient: null}, commands)
          }
        }
      })
    },
    fontSizes () {
      return ['', ...Array(240)
        .fill({})
        .map((_, i) => `${i + 1}`)]
    },
    setWeight (val) {
      this.$set(this.config.resolutionStyle.lg, 'fontWeight', val)
    },
    onMouseDown (e) {
      if (e.target.closest('.pollite') || e.target.closest('.vc-sketch') || e.target.closest('.v-select') || e.target.closest('.vs__dropdown-menu')) return
      this.backgroundMark.color = null

      this.tipa = null
      this.backgroundColorTip = null
    },
    setCommands (commands) {
      this.tipa = commands
    },
    setBgCommands (commands) {
      this.backgroundColorTip = commands
    },
    resetStyles () {
      const styles = {
        'Headline': this.globalStyles.headline,
        'Paragraph': this.globalStyles.paragraph
      }

      this.config.resolutionStyle.lg = {
        ...this.config.resolutionStyle.lg,
        ...styles[this.section.name],
        fontSize: null,
        fontFamily: null,
        color: null
      }

      this.config.resolutionStyle.sm = {
        ...this.config.resolutionStyle.sm,
        ...styles[this.section.name],
        fontSize: null,
        fontFamily: null,
        color: null
      }
    },
    applyTextColor (val, commands) {
      const {state} = this.editor
      const empty = state.selection.empty
      this.pickedColor = val

      if (empty) {
        this.config.resolutionStyle[this.resolution].color = val
      } else {
        commands.textcolor({color: val})
      }
    },
    applyGradientTextColor ({backgroundGradient}, commands, selection = false) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (empty && !selection) {
        this.$set(this.config.resolutionStyle[this.resolutioner], 'backgroundGradient', backgroundGradient)
      } else {
        commands.textgradientcolor(backgroundGradient)
        window.getSelection()?.removeAllRanges()
      }
    },
    resetColorSettings (colorType, commands) {
      colorType.backgroundType ? this.applyGradientTextColor({backgroundGradient: false}, commands) : this.applyTextColor(null, commands)
    },
    applyBackgroundColor (val, commands) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (!empty) {
        commands.backgroundColor({backgroundColor: val})
      }
    },
    applyTextBackground (val, commands) {
      if (!val) {
        return commands.background()
      }

      commands.background({class: 'asd', background: `url("${val}") no-repeat 0 100%`})
    },
    applyFontWeight (val, commands) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (empty) {
        this.$set(this.config.resolutionStyle[this.resolution], 'fontWeight', val)
      } else {
        setTimeout(() => {
          commands.fontweight({fontWeight: val})
        }, 100)
      }

      this.localFontWeight = null
    },
    getLinkAttributes (attrs) {
      const _attrs = attrs.dataVueHref ? JSON.parse(attrs.dataVueHref) : attrs

      return {
        href: _attrs.href || {},
        target: _attrs.target,
        styleeffect: attrs.styleeffect,
        fromGlobalStyle: attrs.fromGlobalStyle
      }
    },
    setLinkUrl (command, url) {
      setTimeout(() => {
        command({
          href: url.href.slug || url.href.href,
          target: url.target || '_self',
          dataVueHref: JSON.stringify({href: url.href, target: url.target}),
          fromGlobalStyle: !!this.linkAttrs.fromGlobalStyle,
          styleeffect: this.linkAttrs.styleeffect,
          id: `link-${shortid.generate()}`
        })
        this.linkSaveText = 'Saved!'

        setTimeout(() => this.linkSaveText = 'Save changes', 800)
      }, 100)
    },
    deleteLinkUrl (commands) {
      this.setLinkUrl(commands.link, {
        href: '', target: '__self', style: null
      })
      this.linkDeleteText = 'Deleted!'
      setTimeout(() => this.linkDeleteText = 'Delete link', 800)
    },
    getPickedColorAttributes (attrs) {
      this.pickedColor = attrs.color || window.getComputedStyle(this.editor.view.dom).color
      return this.pickedColor
    },
    getPickedGradientColorAttributes (attrs) {
      this.colorType.backgroundType = attrs.raw || this.config.resolutionStyle[this.resolutioner].backgroundGradient ? 'gradient' : null

      if (!attrs.raw && this.config.resolutionStyle[this.resolutioner].backgroundGradient) {
        this.backgroundGradient.backgroundGradient = this.config.resolutionStyle[this.resolutioner].backgroundGradient
      }

      return JSON.stringify(attrs)
    },
    getPickedBackgroundColorAttributes (attrs) {
      this.backgroundColor = attrs.backgroundColor || '#fff'
      return this.backgroundColor
    }
  }
}
</script>

<style lang="scss" scoped>
.menubar {
  .btn-menubar-button {
    border: 1px solid #E2E5EC;
    background-color: #fff;

    i {
      color: #aeb1b4;
    }

    &.is-active i {
      color: #44474A;
    }
  }
}
</style>
